import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Save } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { BackupService } from '@/lib/services/backupService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { CreateScheduleData } from '@/types/backup'

export default function CreateSchedulePage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [tables, setTables] = useState<string[]>([])
  const [formData, setFormData] = useState<CreateScheduleData>({
    name: '',
    type: 'full',
    frequency: 'daily',
    time: '02:00',
    retention_days: 30,
    compression: 'gzip',
    encrypted: false,
    storage_disk: 'local',
    active: true,
  })

  useEffect(() => {
    BackupService.getBackupTables().then(setTables).catch(() => {})
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await BackupService.createSchedule(formData)
      showSuccess('Schedule created successfully')
      navigate('/system-settings/backup-restore/schedules')
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: keyof CreateScheduleData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Create Backup Schedule</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>Set up an automated backup schedule</Text>
          </CardHeader>
          <CardBody overflowY="auto" flex={1} minH={0}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Schedule Name</FormLabel>
                  <Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Enter schedule name" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Backup Type</FormLabel>
                  <Select value={formData.type} onChange={(e) => handleChange('type', e.target.value as CreateScheduleData['type'])}>
                    <option value="database">Database Only</option>
                    <option value="files">Files Only</option>
                    <option value="full">Full Backup (Database + Files)</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Frequency</FormLabel>
                  <Select value={formData.frequency} onChange={(e) => handleChange('frequency', e.target.value as CreateScheduleData['frequency'])}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom (Cron Expression)</option>
                  </Select>
                </FormControl>
                {formData.frequency !== 'custom' && (
                  <FormControl>
                    <FormLabel>Time</FormLabel>
                    <Input type="time" value={formData.time || '02:00'} onChange={(e) => handleChange('time', e.target.value)} />
                  </FormControl>
                )}
                {formData.frequency === 'weekly' && (
                  <FormControl>
                    <FormLabel>Day of Week</FormLabel>
                    <Select value={formData.day_of_week ?? 1} onChange={(e) => handleChange('day_of_week', parseInt(e.target.value, 10))}>
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                    </Select>
                  </FormControl>
                )}
                {formData.frequency === 'monthly' && (
                  <FormControl>
                    <FormLabel>Day of Month</FormLabel>
                    <NumberInput value={formData.day_of_month ?? 1} onChange={(_, v) => handleChange('day_of_month', v)} min={1} max={31}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                )}
                {formData.frequency === 'custom' && (
                  <FormControl>
                    <FormLabel>Cron Expression</FormLabel>
                    <Input value={formData.cron_expression || ''} onChange={(e) => handleChange('cron_expression', e.target.value)} placeholder="0 2 * * *" />
                    <Text mt={2} fontSize="sm" color="gray.500">Example: 0 2 * * * (Daily at 2 AM)</Text>
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel>Compression</FormLabel>
                  <Select value={formData.compression ?? 'gzip'} onChange={(e) => handleChange('compression', e.target.value as CreateScheduleData['compression'])}>
                    <option value="none">None</option>
                    <option value="gzip">Gzip</option>
                    <option value="zip">Zip</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Storage Disk</FormLabel>
                  <Select value={formData.storage_disk ?? 'local'} onChange={(e) => handleChange('storage_disk', e.target.value as CreateScheduleData['storage_disk'])}>
                    <option value="local">Local Storage</option>
                    <option value="s3">S3 Storage</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <Checkbox isChecked={formData.encrypted ?? false} onChange={(e) => handleChange('encrypted', e.target.checked)}>Encrypt Backups</Checkbox>
                </FormControl>
                {(formData.type === 'database' || formData.type === 'full') && (
                  <FormControl>
                    <HStack justify="space-between" mb={2}>
                      <FormLabel mb={0}>Select Tables (leave empty for all)</FormLabel>
                      {tables.length > 0 && (
                        <HStack spacing={2}>
                          <Text fontSize="sm" color={colors.textMuted}>{(formData.tables_included?.length || 0)} of {tables.length} selected</Text>
                          <Button size="xs" variant="outline" onClick={() => handleChange('tables_included', (formData.tables_included?.length ?? 0) === tables.length ? undefined : tables)}>
                            {(formData.tables_included?.length ?? 0) === tables.length ? 'Uncheck All' : 'Check All'}
                          </Button>
                        </HStack>
                      )}
                    </HStack>
                    {tables.length > 0 ? (
                      <Box border="1px" borderColor={colors.border} borderRadius="md" p={4} maxH="300px" overflowY="auto" bg={colors.sectionBg}>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                          {tables.map((t) => (
                            <Checkbox key={t} isChecked={formData.tables_included?.includes(t) ?? false} onChange={(e) => {
                              const cur = formData.tables_included ?? []
                              const upd = e.target.checked ? [...cur, t] : cur.filter((x) => x !== t)
                              handleChange('tables_included', upd.length > 0 ? upd : undefined)
                            }}>{t}</Checkbox>
                          ))}
                        </SimpleGrid>
                      </Box>
                    ) : (
                      <Box p={4} border="1px" borderColor={colors.border} borderRadius="md" bg={colors.sectionBg}><Text fontSize="sm" color={colors.textMuted}>Loading tables...</Text></Box>
                    )}
                    <Text mt={2} fontSize="xs" color={colors.textMuted}>
                      {formData.tables_included?.length ? `Selected: ${formData.tables_included.join(', ')}` : 'No tables selected - all tables will be backed up'}
                    </Text>
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel>Retention Days</FormLabel>
                  <NumberInput value={formData.retention_days ?? 30} onChange={(_, v) => handleChange('retention_days', v)} min={1}><NumberInputField /></NumberInput>
                </FormControl>
                <FormControl>
                  <Checkbox isChecked={formData.active ?? true} onChange={(e) => handleChange('active', e.target.checked)}>Active</Checkbox>
                </FormControl>
                <HStack justify="flex-end">
                  <Button variant="ghost" onClick={() => navigate('/system-settings/backup-restore/schedules')}>Cancel</Button>
                  <Button type="submit" colorScheme="blue" leftIcon={<Save size={18} />} isLoading={isLoading} loadingText="Creating...">Create Schedule</Button>
                </HStack>
              </VStack>
            </form>
          </CardBody>
        </Card>
      </Box>
    </AdminLayout>
  )
}
